import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, ArrowRight, Clock, ShieldCheck, ExternalLink, Quote, Building2,
  CheckCircle2, AlertCircle, Target, Lightbulb, BookmarkPlus, PartyPopper,
  Award, Download, Check,
} from 'lucide-react';
import VisualMap, { PILLAR_THEME } from '../components/learning/VisualMap';
import CoachPanel from '../components/learning/CoachPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Eyebrow = ({ children, color }) => (
  <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color }}>{children}</p>
);

const LearningCourse = ({ courseId, onBackToHub = () => {}, onMyLearning = () => {} }) => {
  const userId = localStorage.getItem('cness_username') || 'guest';
  const orgId = sessionStorage.getItem('cness_organization_id') || '';

  const [course, setCourse] = useState(null);
  const [chapter, setChapter] = useState(-1); // -1 = course landing
  const [choice, setChoice] = useState(null);
  const [notes, setNotes] = useState({});
  const [savedActions, setSavedActions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [checkResult, setCheckResult] = useState(null);
  const [completion, setCompletion] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/learning-hub/courses/${courseId}?user_id=${encodeURIComponent(userId)}`)
      .then((r) => r.json())
      .then((d) => {
        setCourse(d);
        setNotes(d.my_reflection?.notes || {});
        setSavedActions((d.my_actions || []).map((a) => a.action_text));
        if (d.my_certificate) setCompletion({ certificate: d.my_certificate });
      });
  }, [courseId, userId]);

  const theme = useMemo(() => PILLAR_THEME[course?.pillar_number] || PILLAR_THEME[1], [course]);

  const persist = async (index, status) => {
    const res = await fetch(`${BACKEND_URL}/api/learning-hub/progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        user_email: userId.includes('@') ? userId : null,
        learner_name: userId.split('@')[0],
        organization_id: orgId,
        course_id: courseId,
        chapter_index: index,
        status,
      }),
    });
    return res.json();
  };

  const go = (index) => {
    const bounded = Math.max(0, Math.min(index, course.chapters.length - 1));
    setChapter(bounded);
    persist(bounded, 'in_progress');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const finish = async () => {
    const res = await persist(course.chapters.length - 1, 'completed');
    const cert = (res.certificates_issued || []).find((c) => c.type === 'pillar');
    const scholar = (res.certificates_issued || []).find((c) => c.type === 'scholar');
    setCompletion({
      certificate: cert || course.my_certificate || null,
      scholar: scholar || null,
      courses_completed: res.courses_completed,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveReflection = async (next) => {
    setNotes(next);
    await fetch(`${BACKEND_URL}/api/learning-hub/reflection`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, organization_id: orgId, course_id: courseId, notes: next }),
    });
  };

  const saveAction = async (text) => {
    setSavedActions((prev) => [...prev, text]);
    await fetch(`${BACKEND_URL}/api/learning-hub/action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, organization_id: orgId, course_id: courseId, action_text: text }),
    });
  };

  const submitCheck = async (skipped) => {
    const res = await fetch(`${BACKEND_URL}/api/learning-hub/knowledge-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, organization_id: orgId, course_id: courseId, answers, skipped }),
    });
    setCheckResult(await res.json());
  };

  if (!course) return <div className="min-h-[50vh] flex items-center justify-center text-slate-400">Loading course…</div>;

  /* ---------- Course landing ---------- */
  if (chapter === -1) {
    const resumeAt = course.progress?.chapter_index || 0;
    const done = course.progress?.status === 'completed';
    return (
      <div className="space-y-12" data-testid="course-landing">
        <div className="rounded-3xl text-white px-6 sm:px-10 py-10 sm:py-12" style={{ backgroundColor: theme.primary }}>
          <button onClick={onBackToHub} data-testid="back-to-hub" className="inline-flex items-center gap-2 text-sm font-medium text-white/80 hover:text-white mb-8">
            <ArrowLeft className="h-4 w-4" /> All six pillars
          </button>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/70 mb-3">Pillar {course.pillar_number} of 6</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">{course.title}</h1>
          <p className="text-base md:text-lg text-white/90 mt-5 max-w-3xl leading-relaxed">{course.central_story}</p>
          <div className="flex flex-wrap items-center gap-5 mt-8 text-sm text-white/80">
            <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> {course.duration_minutes} min</span>
            <span>{course.level}</span>
            <span>{course.chapters.length} chapters</span>
            {done && <span className="inline-flex items-center gap-1.5 font-semibold text-white"><CheckCircle2 className="h-4 w-4" /> Completed</span>}
          </div>
          <button
            onClick={() => go(done ? 0 : resumeAt)}
            data-testid="start-course-btn"
            className="mt-9 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white font-semibold hover:scale-[1.02] transition-transform"
            style={{ color: theme.primary }}
          >
            {done ? 'Revisit course' : course.progress?.started ? `Resume · ${course.progress.chapter_label}` : 'Start chapter 1'}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        <section data-testid="landing-chapter-map">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-2">How this course runs</h2>
          <p className="text-base text-slate-500 mb-6">Five chapters, in this order. You can jump between them at any time.</p>
          <div className="space-y-3">
            {course.chapters.map((ch, i) => (
              <button
                key={ch.key}
                onClick={() => go(i)}
                data-testid={`landing-chapter-${ch.number}`}
                className="w-full text-left flex items-start gap-4 bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
              >
                <span
                  className="h-8 w-8 shrink-0 rounded-full text-white text-sm font-bold flex items-center justify-center"
                  style={{ backgroundColor: (course.progress?.chapter_index ?? 0) >= i && course.progress?.started ? theme.primary : '#CBD5E1' }}
                >
                  {ch.number}
                </span>
                <div className="flex-1">
                  <p className="text-base font-semibold text-slate-900">{ch.label}</p>
                  <p className="text-sm text-slate-500 mt-0.5">{ch.promise}</p>
                </div>
                <span className="text-xs text-slate-400 shrink-0 mt-1">{ch.minutes} min</span>
              </button>
            ))}
          </div>
        </section>

        <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4">
          <ShieldCheck className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-900">{course.firewall_notice}</p>
        </div>

        <section>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-6">What you will learn</h2>
          <ul className="space-y-3">
            {course.outcomes.map((o) => (
              <li key={o} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" style={{ color: theme.primary }} />
                <span className="text-base text-slate-700 leading-relaxed">{o}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-7">
            <Eyebrow color={theme.primary}>This pillar covers</Eyebrow>
            <ul className="space-y-2">{course.covers.map((c) => <li key={c} className="text-sm text-slate-700">• {c}</li>)}</ul>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-7">
            <Eyebrow color="#64748B">This pillar does not cover</Eyebrow>
            <ul className="space-y-2">{course.does_not_cover.map((c) => <li key={c} className="text-sm text-slate-500">• {c}</li>)}</ul>
          </div>
        </section>
      </div>
    );
  }

  /* ---------- Chapter player ---------- */
  const current = course.chapters[chapter];
  const isLast = chapter === course.chapters.length - 1;

  const Stage = ({ stageKey }) => {
    switch (stageKey) {
      case 'hook':
        return (
          <div data-testid="stage-hook">
            <Eyebrow color={theme.primary}>Story</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mb-6">{course.hook.title}</h2>
            <p className="text-lg md:text-xl text-slate-700 leading-relaxed font-light">{course.hook.body}</p>
            <div className="mt-8 rounded-2xl p-6" style={{ backgroundColor: theme.bg }}>
              <p className="text-base font-semibold text-slate-900">{course.hook.reflection_prompt}</p>
              <p className="text-sm text-slate-500 mt-1">Hold the answer in mind — you will come back to it in chapter 4.</p>
            </div>
          </div>
        );
      case 'concepts':
        return (
          <div data-testid="stage-concepts">
            <Eyebrow color={theme.primary}>The idea</Eyebrow>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-8">{course.subtitle}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {course.concepts.map((c, i) => (
                <div key={c.title} data-testid={`concept-card-${i}`} className="bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-lg transition-shadow">
                  <span className="text-xs font-bold" style={{ color: theme.accent }}>0{i + 1}</span>
                  <h3 className="text-xl font-semibold text-slate-900 mt-2 mb-3">{c.title}</h3>
                  <p className="text-base text-slate-600 leading-relaxed">{c.body}</p>
                  <p className="text-sm text-slate-500 mt-4 pl-4 border-l-2" style={{ borderColor: theme.accent }}>{c.example}</p>
                </div>
              ))}
            </div>
          </div>
        );
      case 'visual_map':
        return <div data-testid="stage-visual-map"><VisualMap type={course.visual_map_type} pillarNumber={course.pillar_number} /></div>;
      case 'leader_lens':
        return (
          <div data-testid="stage-leader-lens">
            <Eyebrow color={theme.primary}>Leader Lens</Eyebrow>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-3">Learn from one idea, not a reputation</h2>
            <p className="text-base text-slate-500 mb-8">CNESS curates the idea. Inclusion here is not an endorsement.</p>
            <div className="space-y-6">
              {course.leader_lens.map((l, i) => (
                <div key={l.name} data-testid={`leader-lens-${i}`} className="bg-white rounded-2xl border border-slate-200 p-8">
                  <Quote className="h-6 w-6 mb-4" style={{ color: theme.accent }} />
                  <h3 className="text-2xl font-semibold text-slate-900">{l.name}</h3>
                  <p className="text-base font-medium mt-1" style={{ color: theme.primary }}>{l.idea}</p>
                  <p className="text-base text-slate-600 leading-relaxed mt-4">{l.context}</p>
                  <div className="mt-6 pl-5 border-l-4" style={{ borderColor: theme.primary }}>
                    <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: theme.primary }}>CNESS learning question</p>
                    <p className="text-base md:text-lg text-slate-900 font-medium">{l.learning_question}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'video_lens':
        return (
          <div data-testid="stage-video-lens">
            <Eyebrow color={theme.primary}>Video Lens · optional</Eyebrow>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-3">Six talks, if you want to go deeper</h2>
            <p className="text-base text-slate-500 mb-8">These open on the official source in a new tab — CNESS links rather than re-hosts.</p>
            <div className="space-y-4">
              {course.videos.map((v, i) => (
                <a
                  key={v.url}
                  href={v.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`video-link-${i}`}
                  className="group block bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:border-slate-300 transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 group-hover:underline decoration-2 underline-offset-4">{v.title}</h3>
                      <p className="text-sm text-slate-600 mt-1">{v.speaker} · {v.why_watch}</p>
                      <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {v.duration}</span>
                        <span>Source: {v.source}</span>
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 font-medium">
                          {v.rights_status === 'link_only' ? 'Link only — opens at source' : v.rights_status}
                        </span>
                      </div>
                    </div>
                    <ExternalLink className="h-5 w-5 text-slate-400 group-hover:text-slate-700 shrink-0" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        );
      case 'case_lens':
        return (
          <div data-testid="stage-case-lens">
            <Eyebrow color={theme.primary}>Case Lens</Eyebrow>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-8">Documented practice</h2>
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="flex items-center gap-3 mb-5">
                <Building2 className="h-6 w-6" style={{ color: theme.primary }} />
                <h3 className="text-2xl font-semibold text-slate-900">{course.case_lens.organization}</h3>
              </div>
              <p className="text-base text-slate-600 leading-relaxed">{course.case_lens.practice}</p>
              <div className="mt-6 rounded-xl p-5" style={{ backgroundColor: theme.bg }}>
                <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: theme.primary }}>What CNESS learns</p>
                <p className="text-base text-slate-800">{course.case_lens.what_cness_learns}</p>
              </div>
              <div className="mt-5 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-slate-500">{course.case_lens.caveat}</p>
              </div>
              <p className="text-xs text-slate-400 mt-4">Source: {course.case_lens.source}</p>
            </div>
          </div>
        );
      case 'scenario':
        return (
          <div data-testid="stage-scenario">
            <Eyebrow color={theme.primary}>Scenario Lab</Eyebrow>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-6">Make the decision first</h2>
            <p className="text-lg text-slate-700 leading-relaxed">{course.scenario.situation}</p>
            <p className="text-base font-semibold text-slate-900 mt-6">{course.scenario.question}</p>
            <p className="text-sm text-slate-500 mt-1">No score. No certification consequence.</p>
            <div className="space-y-3 mt-7">
              {course.scenario.options.map((o, i) => (
                <button
                  key={o.label}
                  onClick={() => setChoice(i)}
                  data-testid={`scenario-option-${i}`}
                  className={`w-full text-left rounded-2xl border-2 p-6 transition-all ${choice === i ? 'bg-white shadow-lg' : 'bg-white/70 border-slate-200 hover:border-slate-300'}`}
                  style={choice === i ? { borderColor: theme.primary } : {}}
                >
                  <p className="text-base font-semibold text-slate-900">{o.label}</p>
                  {choice === i && (
                    <p className="text-base text-slate-600 leading-relaxed mt-3" data-testid={`scenario-consequence-${i}`}>{o.consequence}</p>
                  )}
                </button>
              ))}
            </div>
            {choice !== null && (
              <div className="mt-7 rounded-2xl p-6" style={{ backgroundColor: theme.bg }} data-testid="scenario-debrief">
                <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: theme.primary }}>Debrief</p>
                <p className="text-base text-slate-800 leading-relaxed">{course.scenario.debrief}</p>
              </div>
            )}
          </div>
        );
      case 'reflection':
        return (
          <div data-testid="stage-reflection">
            <Eyebrow color={theme.primary}>Reflection · private</Eyebrow>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-3">Where does this show up for you?</h2>
            <p className="text-base text-slate-500 mb-8">Private to you. Your organisation sees participation only, never your words.</p>
            <div className="space-y-5">
              {course.reflection_prompts.map((p, i) => (
                <div key={p} className="bg-white rounded-2xl border border-slate-200 p-6">
                  <label className="block text-base font-semibold text-slate-900 mb-3">{p}</label>
                  <textarea
                    data-testid={`reflection-input-${i}`}
                    value={notes[`q${i}`] || ''}
                    onChange={(e) => setNotes({ ...notes, [`q${i}`]: e.target.value })}
                    onBlur={() => saveReflection(notes)}
                    rows={3}
                    className="w-full rounded-xl border border-slate-200 p-4 text-base text-slate-700 focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': theme.accent }}
                    placeholder="Write freely — only you can read this."
                  />
                </div>
              ))}
            </div>
          </div>
        );
      case 'actions':
        return (
          <div data-testid="stage-actions">
            <Eyebrow color={theme.primary}>Try This Tomorrow</Eyebrow>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-8">Pick one move you will actually make</h2>
            <div className="space-y-4">
              {course.actions.map((a, i) => {
                const saved = savedActions.includes(a);
                return (
                  <div key={a} data-testid={`action-row-${i}`} className="bg-white rounded-2xl border border-slate-200 p-6 flex items-start justify-between gap-5">
                    <div className="flex items-start gap-3">
                      <Target className="h-5 w-5 shrink-0 mt-0.5" style={{ color: theme.primary }} />
                      <p className="text-base text-slate-800 leading-relaxed">{a}</p>
                    </div>
                    <button
                      onClick={() => !saved && saveAction(a)}
                      disabled={saved}
                      data-testid={`save-action-${i}`}
                      className={`shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors ${saved ? 'bg-emerald-50 text-emerald-700' : 'text-white'}`}
                      style={saved ? {} : { backgroundColor: theme.primary }}
                    >
                      {saved ? <><CheckCircle2 className="h-4 w-4" /> Saved</> : <><BookmarkPlus className="h-4 w-4" /> Save action</>}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      case 'takeaways':
        return (
          <div data-testid="stage-takeaways">
            <Eyebrow color={theme.primary}>Remember this</Eyebrow>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-8">What is worth keeping</h2>
            <div className="space-y-4">
              {course.takeaways.map((t, i) => (
                <div key={t} data-testid={`takeaway-${i}`} className="flex items-start gap-4 bg-white rounded-2xl border border-slate-200 p-6">
                  <span className="text-2xl font-bold" style={{ color: theme.accent }}>0{i + 1}</span>
                  <p className="text-base md:text-lg text-slate-800 leading-relaxed">{t}</p>
                </div>
              ))}
            </div>
          </div>
        );
      case 'knowledge_check':
        return (
          <div data-testid="stage-knowledge-check">
            <Eyebrow color={theme.primary}>Optional check</Eyebrow>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-3">Three questions, zero consequence</h2>
            <p className="text-base text-slate-500 mb-8">Skippable, and never part of your CIS score or certification.</p>

            {!checkResult ? (
              <>
                <div className="space-y-6">
                  {course.knowledge_check.map((q, qi) => (
                    <div key={q.question} className="bg-white rounded-2xl border border-slate-200 p-7">
                      <p className="text-base md:text-lg font-semibold text-slate-900 mb-4">{qi + 1}. {q.question}</p>
                      <div className="space-y-2">
                        {q.options.map((opt, oi) => (
                          <label key={opt} className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-colors ${answers[qi] === oi ? 'bg-slate-100' : 'hover:bg-slate-50'}`}>
                            <input
                              type="radio"
                              name={`q${qi}`}
                              checked={answers[qi] === oi}
                              onChange={() => setAnswers({ ...answers, [qi]: oi })}
                              data-testid={`check-q${qi}-opt${oi}`}
                            />
                            <span className="text-base text-slate-700">{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-8">
                  <button onClick={() => submitCheck(true)} data-testid="check-skip-btn" className="px-6 py-3 rounded-full border-2 border-slate-300 text-slate-600 font-semibold hover:bg-white">
                    Skip this check
                  </button>
                  <button
                    onClick={() => submitCheck(false)}
                    disabled={Object.keys(answers).length < course.knowledge_check.length}
                    data-testid="check-submit-btn"
                    className="px-7 py-3 rounded-full text-white font-semibold disabled:bg-slate-300"
                    style={Object.keys(answers).length < course.knowledge_check.length ? {} : { backgroundColor: theme.primary }}
                  >
                    Check my understanding
                  </button>
                </div>
              </>
            ) : (
              <div data-testid="check-result">
                <div className="rounded-2xl p-7 mb-6" style={{ backgroundColor: theme.bg }}>
                  <p className="text-2xl font-semibold text-slate-900">
                    {checkResult.skipped ? 'Skipped — no problem.' : `${checkResult.correct} of ${checkResult.total} correct`}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">{checkResult.firewall_notice}</p>
                </div>
                <div className="space-y-4">
                  {checkResult.feedback.map((f) => (
                    <div key={f.question_index} className={`rounded-2xl border-2 p-6 ${f.is_correct ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
                      <div className="flex items-start gap-3">
                        {f.is_correct ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" /> : <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />}
                        <div>
                          <p className="text-base font-semibold text-slate-900">{f.question}</p>
                          {!f.is_correct && <p className="text-sm text-slate-700 mt-2">Correct answer: <strong>{f.correct_option}</strong></p>}
                          <p className="text-sm text-slate-600 mt-1">{f.explanation}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  /* ---------- Completion ---------- */
  if (isLast && completion) {
    const cert = completion.certificate;
    return (
      <div className="space-y-10" data-testid="stage-completion">
        <div className="rounded-3xl bg-white border border-slate-200 p-10 text-center">
          <PartyPopper className="h-12 w-12 mx-auto mb-6" style={{ color: theme.primary }} />
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">{course.title} complete</h2>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            You have one idea, one decision you practised and {savedActions.length} saved action{savedActions.length === 1 ? '' : 's'}.
            We will nudge you in 24 hours to reflect, and in 7 days to revisit.
          </p>
          {completion.courses_completed != null && (
            <p className="text-sm font-semibold text-slate-500 mt-4" data-testid="completion-progress">
              {completion.courses_completed} of 6 pillars complete
            </p>
          )}

          {cert && (
            <div className="mt-8 inline-flex flex-col items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-8 py-7" data-testid="completion-certificate">
              <Award className="h-8 w-8" style={{ color: theme.primary }} />
              <div>
                <p className="text-base font-semibold text-slate-900">{cert.title} — learning certificate</p>
                <p className="text-xs text-slate-500 mt-1">{cert.certificate_id}</p>
              </div>
              <a
                href={`${BACKEND_URL}/api/learning-hub/certificate/${cert.certificate_id}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="completion-certificate-download"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold"
                style={{ backgroundColor: theme.primary }}
              >
                <Download className="h-4 w-4" /> Download certificate
              </a>
            </div>
          )}

          {completion.scholar && (
            <div className="mt-6 rounded-2xl border-2 border-amber-300 bg-amber-50 p-6 max-w-xl mx-auto" data-testid="scholar-certificate">
              <p className="text-base font-bold text-amber-900">Six-Pillar Scholar</p>
              <p className="text-sm text-amber-800 mt-1">
                You have completed all six pillar courses. Your scholar certificate is in My Learning.
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
            <button onClick={onBackToHub} data-testid="completion-next-btn" className="px-7 py-3.5 rounded-full text-white font-semibold" style={{ backgroundColor: theme.primary }}>
              Choose my next pillar
            </button>
            <button onClick={onMyLearning} data-testid="completion-mylearning-btn" className="px-7 py-3.5 rounded-full border-2 border-slate-300 text-slate-700 font-semibold hover:bg-white">
              View my learning
            </button>
          </div>

          <div className="mt-10 inline-flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-left max-w-2xl">
            <Lightbulb className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-900">This is a learning completion record — it is not CNESS certification and does not affect your CIS score.</p>
          </div>
        </div>
      </div>
    );
  }

  const visibleStages = current.stages.filter((s) => s !== 'completion');

  return (
    <div data-testid="course-player">
      {/* Chapter stepper — you are here */}
      <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 bg-white/90 backdrop-blur-xl border-b border-slate-200 py-4 mb-10">
        <div className="flex items-center justify-between gap-4 mb-3">
          <button onClick={() => setChapter(-1)} data-testid="player-back-btn" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" /> Pillar {course.pillar_number} · {course.title}
          </button>
          <span className="text-xs font-semibold text-slate-500" data-testid="player-chapter-label">
            Chapter {current.number} of {course.chapters.length} · {current.label}
          </span>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto" data-testid="chapter-stepper">
          {course.chapters.map((ch, i) => {
            const state = i < chapter ? 'done' : i === chapter ? 'current' : 'todo';
            return (
              <button
                key={ch.key}
                onClick={() => go(i)}
                data-testid={`chapter-step-${ch.number}`}
                aria-current={state === 'current'}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                  state === 'current' ? 'text-white' : state === 'done' ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
                style={state === 'current' ? { backgroundColor: theme.primary } : {}}
              >
                {state === 'done' ? <Check className="h-3.5 w-3.5" /> : <span>{ch.number}</span>}
                {ch.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-4xl">
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">{current.label}</h1>
          <p className="text-base text-slate-500 mt-2">{current.promise}</p>
        </div>

        <div className="space-y-16">
          {visibleStages.map((s) => <Stage key={s} stageKey={s} />)}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 mt-16 pt-8 border-t border-slate-200">
          <button
            onClick={() => (chapter === 0 ? setChapter(-1) : go(chapter - 1))}
            data-testid="player-prev-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-slate-300 text-slate-700 font-semibold hover:bg-white"
          >
            <ArrowLeft className="h-4 w-4" /> {chapter === 0 ? 'Course overview' : `Chapter ${chapter}`}
          </button>
          {isLast ? (
            <button
              onClick={finish}
              data-testid="finish-course-btn"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-semibold"
              style={{ backgroundColor: theme.primary }}
            >
              Finish course <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={() => go(chapter + 1)}
              data-testid="player-next-btn"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-semibold"
              style={{ backgroundColor: theme.primary }}
            >
              Chapter {current.number + 1} · {course.chapters[chapter + 1].label} <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <CoachPanel courseId={courseId} courseTitle={course.title} chapterLabel={current.label} theme={theme} />
    </div>
  );
};

export default LearningCourse;
